import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
	Box,
	Chip,
	IconButton,
	Popover,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
	Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FilterList } from "./FilterList";
import { useTranslation } from "react-i18next";
import { CustomDateRangePicker } from "./CustomDateRangePicker";
import { filterDatePeriods } from "./filterDatePeriods";

export function EnhancedTableToolbar({
	titleText,
	onAddClick,
	filterParams,
	addFilterParams,
	removeFilterParams,
	wallets,
	selectedType,
	setSelectedType,
	selectedCategory,
	setSelectedCategory,
	selectedSubcategory,
	setSelectedSubcategory,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const [anchorDateRange, setAnchorDateRange] = useState(null);
	const [anchorFilterList, setAnchorFilterList] = useState(null);

	const open = Boolean(anchorFilterList);
	const filterId = open ? "simple-popover" : undefined;

	const filtersBoxStyle = {
		display: "flex",
		flexWrap: "wrap",
		bgcolor: theme.palette.background.default,
		p: 2,
		borderRadius: "10px",
		gap: 1,
		mt: 1,
	};

	const isSameDay = (d1, d2) =>
		d1?.getFullYear() === d2?.getFullYear() &&
		d1?.getMonth() === d2?.getMonth() &&
		d1?.getDate() === d2?.getDate();

	const findMatchingPeriod = (range) => {
		return filterDatePeriods.find(
			(period) =>
				isSameDay(period.startDate, range.startDate) &&
				isSameDay(period.endDate, range.endDate)
		);
	};

	const handleApplyRange = ({ startDate, endDate }) => {
		const matched = findMatchingPeriod({ startDate, endDate });

		if (matched) {
			addFilterParams("datePeriod", {
				label: matched.label,
				startDate: matched.startDate,
				endDate: matched.endDate,
			});
		} else {
			addFilterParams("datePeriod", {
				label: t("customRange"),
				startDate: startDate,
				endDate: endDate,
			});
		}

		setAnchorDateRange(null);
	};

	return (
		<Toolbar sx={{ pl: { sm: 3 }, pr: { xs: 1, sm: 2 }, py: 2 }}>
			<Box
				sx={{ display: "flex", flexDirection: "column", width: "100%" }}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="h6" id="tableTitle">
						{titleText}
					</Typography>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
						}}
					>
						<TextField
							sx={{
								width: 200,
							}}
							label={t("search")}
							value={filterParams.searchPhrase}
							onChange={(e) =>
								addFilterParams("searchPhrase", e.target.value)
							}
						/>

						<Button
							variant="outlined"
							endIcon={<ArrowDropDownIcon />}
							sx={{ mx: 1 }}
							onClick={(e) => setAnchorDateRange(e.currentTarget)}
						>
							{t(filterParams.datePeriod.label)}
						</Button>

						{!!anchorDateRange && (
							<Popover
								disableEnforceFocus
								open={Boolean(anchorDateRange)}
								anchorEl={anchorDateRange}
								onClose={() => setAnchorDateRange(null)}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "left",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "left",
								}}
								PaperProps={{
									sx: {
										borderRadius: "10px",
									},
								}}
							>
								<CustomDateRangePicker
									initialStartDate={
										filterParams.datePeriod.startDate
									}
									initialEndDate={
										filterParams.datePeriod.endDate
									}
									onApply={handleApplyRange}
									onCancel={() => setAnchorDateRange(null)}
								/>
							</Popover>
						)}

						<Tooltip title={t("filterTransactions")}>
							<IconButton
								aria-describedby={filterId}
								onClick={(e) =>
									setAnchorFilterList(e.currentTarget)
								}
							>
								<FilterListIcon />
							</IconButton>
						</Tooltip>

						{!!anchorFilterList && (
							<Popover
								disableEnforceFocus
								id={filterId}
								open={open}
								anchorEl={anchorFilterList}
								onClose={() => setAnchorFilterList(null)}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "center",
								}}
								slotProps={{
									paper: {
										sx: {
											borderRadius: "10px",
										},
									},
								}}
							>
								<FilterList
									wallets={wallets}
									filterParams={filterParams}
									addFilterParams={addFilterParams}
								/>
							</Popover>
						)}

						<Tooltip
							title={t("addCashTransaction")}
							onClick={onAddClick}
						>
							<IconButton>
								<AddIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
				{(filterParams.walletFilters.length > 0 ||
					filterParams.typeFilters.length > 0 ||
					filterParams.importantFilters.length > 0 ||
					selectedType ||
					selectedCategory ||
					selectedSubcategory) && (
					<Box sx={filtersBoxStyle}>
						{filterParams.walletFilters.map((wallet) => (
							<Chip
								key={wallet}
								sx={{ bgcolor: theme.palette.background.paper }}
								label={
									wallets.find((w) => w._id === wallet)?.name
								}
								onDelete={() =>
									removeFilterParams("walletFilters", wallet)
								}
							/>
						))}

						{filterParams.typeFilters.map((type) => (
							<Chip
								key={type}
								sx={{
									bgcolor: theme.palette.background.paper,
								}}
								label={t(type)}
								onDelete={() =>
									removeFilterParams("typeFilters", type)
								}
							/>
						))}

						{filterParams.importantFilters.map((filter) => (
							<Chip
								key={filter}
								sx={{
									bgcolor: theme.palette.background.paper,
								}}
								label={t(filter)}
								onDelete={() =>
									removeFilterParams(
										"importantFilters",
										filter
									)
								}
							/>
						))}

						{selectedSubcategory && (
							<Chip
								sx={{
									bgcolor: theme.palette.background.paper,
								}}
								label={
									t("category") +
									": " +
									(selectedCategory.groupName
										? selectedCategory.groupName
										: t(
												selectedCategory.groupTranslationKey
											)) +
									" - " +
									(selectedSubcategory.name
										? selectedSubcategory.name
										: t(selectedSubcategory.translationKey))
								}
								onDelete={() => {
									setSelectedCategory(null);
									setSelectedSubcategory(null);
									setSelectedType(null);
								}}
							/>
						)}

						{selectedCategory && !selectedSubcategory && (
							<Chip
								sx={{
									bgcolor: theme.palette.background.paper,
								}}
								label={
									t("category") +
									": " +
									(selectedCategory.groupName
										? selectedCategory.groupName
										: t(
												selectedCategory.groupTranslationKey
											))
								}
								onDelete={() => {
									setSelectedCategory(null);
									setSelectedType(null);
								}}
							/>
						)}

						{selectedType &&
							!selectedCategory &&
							!selectedSubcategory && (
								<Chip
									sx={{
										bgcolor: theme.palette.background.paper,
									}}
									label={t("type") + ": " + t(selectedType)}
									onDelete={() => {
										setSelectedType(null);
									}}
								/>
							)}
					</Box>
				)}
			</Box>
		</Toolbar>
	);
}
